import React, { useState } from "react"
import Modal from "../index"
import Button from "../../Form/Button"
import styles from "./deduplicatedLead.module.css"
import { MdLockOutline, MdOpenInNew, MdOutlineContentCopy, MdOutlineEdit, MdOutlineTaskAlt, MdOutlineVpnKey } from "react-icons/md"
import { requestLeadAccess } from "../../../schemas/Lead"

const matchLabels = {
    phone: "telefone",
    email: "e-mail",
    document: "documento",
    name: "nome",
}

const reasonLabels = {
    open_duplicate: "já existia um lead aberto com esses dados",
    lost_duplicate: "já existia um lead descartado com esses dados",
    won_duplicate: "já existia um lead ganho com esses dados",
    deleted_duplicate: "já existia um lead excluído na lixeira com esses dados",
}

const statusLabels = {
    open_duplicate: "em atendimento",
    lost_duplicate: "descartado",
    won_duplicate: "ganho",
    deleted_duplicate: "na lixeira",
}

const formatMatchValue = (field, value) => {
    const rawValue = String(value || "").trim()
    if (!rawValue) return ""

    if (field !== "phone") return rawValue

    const numbers = rawValue.replace(/\D/g, "")
    if (numbers.length === 13 && numbers.startsWith("55")) {
        return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9)}`
    }
    if (numbers.length === 12 && numbers.startsWith("55")) {
        return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 8)}-${numbers.slice(8)}`
    }
    if (numbers.length === 11) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
    }
    if (numbers.length === 10) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`
    }

    return rawValue
}

const ModalDeduplicatedLead = ({ lead, onClose, onOpenLead, onFinish }) => {
    const [accessRequest, setAccessRequest] = useState(lead?.deduplication_reuse?.access_request || null)
    const [requestingAccess, setRequestingAccess] = useState(false)

    if (!lead) return null

    const reuse = lead.deduplication_reuse || {}
    const matchLabel = matchLabels[reuse.match_field] || "dado de contato"
    const reason = reasonLabels[reuse.reason] || "o sistema encontrou um cadastro com os mesmos dados"
    const statusLabel = statusLabels[reuse.reason] || "existente"
    const matchValue = formatMatchValue(reuse.match_field, reuse.match_value)
    const canOpenLead = reuse.can_open_lead !== false
    const isManualExisting = reuse.action === "existing_found" || reuse.is_manual_creation
    const isReactivated = !isManualExisting && Boolean(reuse.was_reactivated || reuse.lead_status === "lost")
    const isDeletedMatch = reuse.reason === "deleted_duplicate" || reuse.matched_type === "trash"
    const accessAlreadyRequested = accessRequest?.status === "pending"
    const modalTitle = isManualExisting ? "Cadastro existente encontrado" : (isReactivated ? "Lead descartado reativado" : (isDeletedMatch ? "Cadastro excluído encontrado" : "Cadastro existente reaproveitado"))
    const modalDescription = isManualExisting
        ? "Nenhum novo lead foi criado. Já existe um cadastro com esses dados."
        : (isReactivated
        ? "Nenhum novo lead foi criado, porque um lead descartado com os mesmos dados foi reativado."
        : (isDeletedMatch
            ? "Encontramos um cadastro excluído com os mesmos dados."
            : "Nenhum novo lead foi criado, porque encontramos um cadastro correspondente."))
    const actionDescription = isManualExisting
        ? `Encontramos um cadastro ${statusLabel} com o mesmo ${matchLabel}${matchValue ? ` (${matchValue})` : ""}. Revise o cadastro existente ou altere os dados para continuar.`
        : (isReactivated
        ? `O lead foi reativado porque ${reason}. A correspondência foi feita por ${matchLabel}`
        : (isDeletedMatch
            ? `A correspondência foi feita por ${matchLabel}`
            : `O cadastro foi reaproveitado porque ${reason}. A correspondência foi feita por ${matchLabel}`))

    const handleOpenLead = () => {
        if (!canOpenLead) return
        onOpenLead?.(lead._id)
        handleFinish()
    }

    const handleFinish = () => {
        if (onFinish) {
            onFinish()
        } else {
            onClose?.()
        }
    }

    const handleRequestAccess = async () => {
        if (!lead?._id || requestingAccess || accessRequest?.status === "pending") return

        try {
            setRequestingAccess(true)
            const request = await requestLeadAccess(lead._id, {
                account_id: lead.account_id,
                reason: "Solicitado a partir de tentativa de criação de lead duplicado.",
            })
            setAccessRequest(request)
        } finally {
            setRequestingAccess(false)
        }
    }

    return (
        <Modal
            icon={<MdOutlineContentCopy />}
            title={modalTitle}
            description={modalDescription}
            closeModal={onClose}
            heightFitContent={true}
        >
            <div className={styles.container}>
                <div className={styles.message}>
                    <span className={styles.label}>Cadastro encontrado</span>
                    <strong>{lead.name || "Lead existente"}</strong>
                    <span>
                        {actionDescription}
                        {!isManualExisting && reuse.match_value ? ` (${reuse.match_value})` : ""}.
                    </span>
                </div>

                {isReactivated &&
                    <div className={styles.notice}>
                        Este lead estava fora do funil por estar descartado/perdido. Ele continuava no histórico do CRM e foi reativado para continuar o atendimento.
                    </div>
                }

                {reuse.same_team === false &&
                    <div className={styles.warning}>
                        O cadastro existente pertence a outra equipe. Mantivemos o lead original para evitar duplicidade.
                    </div>
                }

                {!canOpenLead &&
                    <div className={styles.restricted}>
                        <MdLockOutline />
                        <span>
                            Esse lead está com outro responsável e seu cargo não permite abrir esse cadastro.
                            Solicite acesso para que o responsável ou um gestor libere sua participação.
                        </span>
                    </div>
                }

                {!canOpenLead && accessAlreadyRequested &&
                    <div className={styles.notice}>
                        Acesso já solicitado. Você será notificado quando o responsável aprovar ou negar a solicitação.
                    </div>
                }

                <div className={styles.actions}>
                    {canOpenLead &&
                        <Button variant="secondary" type="button" onClick={handleOpenLead}>
                            <MdOpenInNew /> {isReactivated ? "Abrir lead reativado" : "Abrir cadastro existente"}
                        </Button>
                    }
                    {!canOpenLead &&
                        <Button variant="secondary" type="button" onClick={handleRequestAccess} disabled={requestingAccess || accessAlreadyRequested}>
                            <MdOutlineVpnKey /> {accessAlreadyRequested ? "Acesso já solicitado" : requestingAccess ? "Solicitando..." : "Solicitar acesso"}
                        </Button>
                    }
                    <Button variant="neutral" type="button" onClick={onClose}>
                        <MdOutlineEdit /> Alterar dados
                    </Button>
                    <Button variant="neutral" type="button" onClick={handleFinish}>
                        <MdOutlineTaskAlt /> Finalizar sem criar novo
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

export default ModalDeduplicatedLead
